import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { Modal, Grid, FormField, btnStyle, btnSecStyle } from './CardsPage'

const CURRENCIES = ['eur','usd','gbp','hrk','chf','sek','nok','dkk']
const INTERVALS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
  { value: 'one_time', label: 'One-time' },
]

export default function BillingPage() {
  const { club: authClub } = useAuth()
  const [clubOverride, setClubOverride] = useState(null)
  const club = clubOverride || authClub

  useEffect(() => {
    if (authClub?.id) {
      supabase.from('clubs').select('*').eq('id', authClub.id).single()
        .then(({ data }) => { if (data) setClubOverride(data) })
    }
  }, [authClub?.id])
  const [plans, setPlans] = useState([])
  const [fighters, setFighters] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [payments, setPayments] = useState([])
  const [discountCodes, setDiscountCodes] = useState([])
  const [familyGroups, setFamilyGroups] = useState([])
  const [showAddPlan, setShowAddPlan] = useState(false)
  const [showAddCode, setShowAddCode] = useState(false)
  const [showAddFamily, setShowAddFamily] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [connecting, setConnecting] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!club) return
    const [p, f, s, pay, codes, families] = await Promise.all([
      supabase.from('membership_plans').select('*').eq('club_id', club.id).eq('active', true).order('created_at'),
      supabase.from('fighters').select('*').eq('club_id', club.id).order('first_name'),
      supabase.from('member_subscriptions').select('*, membership_plans(*), fighters(*), discount_codes(*)'),
      supabase.from('payment_history').select('*, fighters(*)').order('created_at', { ascending: false }).limit(20),
      supabase.from('discount_codes').select('*').eq('club_id', club.id).order('created_at', { ascending: false }),
      supabase.from('family_groups').select('*, fighters(*)').eq('club_id', club.id),
    ])
    setPlans(p.data || [])
    setFighters(f.data || [])
    setSubscriptions(s.data || [])
    setPayments(pay.data || [])
    setDiscountCodes(codes.data || [])
    setFamilyGroups(families.data || [])
    setLoading(false)
  }, [club])

  useEffect(() => { load() }, [load])

  // Detect return from Stripe Connect onboarding
  useEffect(() => {
    // Check URL param and store in localStorage
    const params = new URLSearchParams(window.location.search)
    if (params.get('sc') === '1') {
      localStorage.setItem('stripe_connect_pending', '1')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  useEffect(() => {
    if (!club?.id) return
    if (!localStorage.getItem('stripe_connect_pending')) return
    localStorage.removeItem('stripe_connect_pending')
    supabase.from('clubs').update({
      stripe_onboarded: true,
      stripe_connected: true,
    }).eq('id', club.id).then(() => window.location.reload())
  }, [club?.id])

  async function connectStripe() {
    setConnecting(true)
    const { data, error } = await supabase.functions.invoke('stripe-connect', {
      body: { club_id: club.id }
    })
    if (data?.url) window.location.href = data.url
    else { alert('Error: ' + (error?.message || 'Could not connect Stripe')); setConnecting(false) }
  }

  // Stats
  const activeCount = subscriptions.filter(s => s.status === 'active').length
  const overdueCount = subscriptions.filter(s => s.status === 'overdue').length
  const suspendedCount = subscriptions.filter(s => s.status === 'suspended').length
  const monthlyRevenue = subscriptions
    .filter(s => s.status === 'active' && s.membership_plans?.interval === 'month')
    .reduce((sum, s) => sum + (s.discounted_amount || s.membership_plans?.amount || 0), 0)

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>

  return (
    <div style={{ maxWidth: 800 }}>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#f0f2f7', borderRadius: 12, padding: 4, marginBottom: '1.5rem' }}>
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'subscriptions', label: 'Subscriptions' },
          { id: 'discounts', label: 'Discount codes' },
          { id: 'families', label: 'Family plans' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: '8px', borderRadius: 9, border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
            background: activeTab === tab.id ? '#fff' : 'transparent',
            color: activeTab === tab.id ? '#1a1a1a' : '#888',
            boxShadow: activeTab === tab.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s'
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Stripe Connect status */}
      <div style={panelStyle}>
        <div style={panelTitle}>Stripe payments</div>
        {club.stripe_onboarded ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>\n            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1a7040', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Stripe connected</div>
              <div style={{ fontSize: 12, color: '#888' }}>Members can pay directly. Money goes to your Stripe account.</div>
            </div>
            <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer"
              style={{ marginLeft: 'auto', ...btnSecStyle, textDecoration: 'none', padding: '6px 12px', fontSize: 12 }}>
              Stripe dashboard
            </a>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 14, lineHeight: 1.6 }}>
              Connect your Stripe account to start collecting membership fees. Money goes directly to your bank account. Setup takes about 5 minutes.
            </p>
            <button onClick={connectStripe} disabled={connecting} style={{ ...btnStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/></svg>
              {connecting ? 'Connecting...' : 'Connect with Stripe'}
            </button>
          </div>
        )}
      </div>

      {club.stripe_onboarded && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: '1.25rem' }}>
            <StatBox label="Active" value={activeCount} color="#1a7040" />
            <StatBox label="Monthly revenue" value={`${(monthlyRevenue / 100).toFixed(2)} ${plans[0]?.currency?.toUpperCase() || 'EUR'}`} color="#1a1a1a" />
            <StatBox label="Overdue" value={overdueCount} color={overdueCount > 0 ? '#a06000' : '#1a1a1a'} bg={overdueCount > 0 ? '#fff8e8' : '#f8f9fc'} />
            <StatBox label="Suspended" value={suspendedCount} color={suspendedCount > 0 ? '#c00' : '#1a1a1a'} bg={suspendedCount > 0 ? '#fff0f0' : '#f8f9fc'} />
          </div>

          {/* Overdue members alert */}
          {(overdueCount + suspendedCount) > 0 && (
            <div style={{ background: '#fff8e8', border: '1px solid #f8d88a', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#a06000', marginBottom: 10 }}>
                Attention needed ({overdueCount + suspendedCount})
              </div>
              {subscriptions.filter(s => ['overdue','suspended'].includes(s.status)).map(s => {
                const daysSince = s.overdue_since
                  ? Math.floor((Date.now() - new Date(s.overdue_since).getTime()) / (1000 * 60 * 60 * 24))
                  : 0
                const graceDays = club.grace_period_days || 7
                const daysLeft = Math.max(0, graceDays - daysSince)
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f8e8c0' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                        {s.fighters?.first_name} {s.fighters?.last_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
                        {s.status === 'suspended'
                          ? `Suspended · ${s.payment_failed_count || 0} failed payment${s.payment_failed_count !== 1 ? 's' : ''}`
                          : `Overdue ${daysSince} day${daysSince !== 1 ? 's' : ''} · ${daysLeft} day${daysLeft !== 1 ? 's' : ''} until suspension`
                        }
                      </div>
                    </div>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 500,
                      background: s.status === 'suspended' ? '#fff0f0' : '#fff8e8',
                      color: s.status === 'suspended' ? '#c00' : '#a06000',
                      textTransform: 'capitalize'
                    }}>{s.status}</span>
                    <button onClick={async () => {
                      // Manually reactivate (e.g. cash payment received)
                      if (window.confirm(`Mark ${s.fighters?.first_name}'s membership as active? Use this if they paid cash or bank transfer.`)) {
                        await supabase.from('member_subscriptions').update({
                          status: 'active',
                          overdue_since: null,
                          payment_failed_count: 0,
                          suspended_at: null,
                          updated_at: new Date().toISOString()
                        }).eq('id', s.id)
                        await supabase.from('payment_history').insert({
                          fighter_id: s.fighter_id,
                          subscription_id: s.id,
                          plan_id: s.plan_id,
                          amount: s.membership_plans?.amount || 0,
                          currency: s.membership_plans?.currency || 'eur',
                          status: 'succeeded',
                          paid_at: new Date().toISOString(),
                        })
                        load()
                      }
                    }} style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                      Mark paid
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Grace period setting */}
          <div style={{ ...panelStyle, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 2 }}>Grace period</div>
              <div style={{ fontSize: 12, color: '#888' }}>Days before a member is suspended after a failed payment. Stripe retries automatically during this time.</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min="1" max="30" defaultValue={club.grace_period_days || 7}
                style={{ width: 60, padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, textAlign: 'center' }}
                id="grace-days"
              />
              <span style={{ fontSize: 13, color: '#888' }}>days</span>
              <button onClick={async () => {
                const val = parseInt((document.getElementById('grace-days')).value)
                if (val >= 1 && val <= 30) {
                  await supabase.from('clubs').update({ grace_period_days: val }).eq('id', club.id)
                  alert('Grace period updated')
                }
              }} style={{ ...btnSecStyle, padding: '6px 12px', fontSize: 12 }}>Save</button>
            </div>
          </div>
        </>
      )}

      {/* Membership plans — shown on overview and subscriptions tabs */}
      {(activeTab === 'overview' || activeTab === 'subscriptions') && <div style={panelStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ ...panelTitle, margin: 0, flex: 1 }}>Membership plans</div>
          <button onClick={() => setShowAddPlan(true)} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>+ Add plan</button>
        </div>

        {plans.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1.5rem', color: '#888', fontSize: 13 }}>
            No plans yet. Add your first membership plan to start collecting fees.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plans.map(plan => {
              const activeSubs = subscriptions.filter(s => s.plan_id === plan.id && s.status === 'active').length
              return (
                <div key={plan.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8f9fc', borderRadius: 10, border: '1px solid #eee' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{plan.name}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {(plan.amount / 100).toFixed(2)} {plan.currency.toUpperCase()} / {INTERVALS.find(i => i.value === plan.interval)?.label} · {plan.fighter_type}
                    </div>
                    {plan.description && <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{plan.description}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{activeSubs} active</div>
                  </div>
                  <button onClick={async () => {
                    if (window.confirm(`Archive the "${plan.name}" plan?`)) {
                      await supabase.from('membership_plans').update({ active: false }).eq('id', plan.id)
                      load()
                    }
                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 18, lineHeight: 1 }}>×</button>
                </div>
              )
            })}
          </div>
        )}
      </div>}

      {/* Member subscriptions */}
      {(activeTab === 'overview' || activeTab === 'subscriptions') && <div style={panelStyle}>
        <div style={panelTitle}>Member subscriptions</div>
        {subscriptions.length === 0 ? (
          <div style={{ color: '#888', fontSize: 13 }}>No subscriptions yet.</div>
        ) : (
          <div>
            {/* Overdue first */}
            {subscriptions.filter(s => s.status === 'overdue').map(s => (
              <SubRow key={s.id} sub={s} highlight="overdue" onRefresh={load} plans={plans} />
            ))}
            {subscriptions.filter(s => s.status === 'active').map(s => (
              <SubRow key={s.id} sub={s} onRefresh={load} plans={plans} />
            ))}
            {subscriptions.filter(s => !['active','overdue'].includes(s.status)).map(s => (
              <SubRow key={s.id} sub={s} onRefresh={load} plans={plans} />
            ))}
          </div>
        )}

        {/* Fighters without subscription */}
        {fighters.filter(f => !subscriptions.find(s => s.fighter_id === f.id)).length > 0 && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>No plan assigned</div>
            {fighters.filter(f => !subscriptions.find(s => s.fighter_id === f.id)).map(f => (
              <AssignRow key={f.id} fighter={f} plans={plans} onAssigned={load} />
            ))}
          </div>
        )}
      </div>}

      {/* Upcoming renewals this week */}
      {activeTab === 'overview' && (() => {
        const upcoming = subscriptions.filter(s => {
          if (s.status !== 'active' || !s.next_billing_date) return false
          const days = Math.ceil((new Date(s.next_billing_date) - new Date()) / (1000 * 60 * 60 * 24))
          return days >= 0 && days <= 7
        }).sort((a, b) => new Date(a.next_billing_date) - new Date(b.next_billing_date))
        if (!upcoming.length) return null
        return (
          <div style={panelStyle}>
            <div style={panelTitle}>Renewing this week ({upcoming.length})</div>
            {upcoming.map(s => {
              const days = Math.ceil((new Date(s.next_billing_date) - new Date()) / (1000 * 60 * 60 * 24))
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f4f4f4' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{s.fighters?.first_name} {s.fighters?.last_name}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>{s.membership_plans?.name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{(s.membership_plans?.amount / 100).toFixed(2)} {s.membership_plans?.currency?.toUpperCase()}</div>
                    <div style={{ fontSize: 11, color: days === 0 ? '#c00' : '#888' }}>{days === 0 ? 'Today' : `In ${days} day${days !== 1 ? 's' : ''}`}</div>
                  </div>
                </div>
              )
            })}
            <div style={{ marginTop: 10, fontSize: 12, color: '#888', fontWeight: 500 }}>
              Expected: {(upcoming.reduce((sum, s) => sum + (s.membership_plans?.amount || 0), 0) / 100).toFixed(2)} {plans[0]?.currency?.toUpperCase() || 'EUR'}
            </div>
          </div>
        )
      })()}

      {/* Recent payments */}
      {activeTab === 'overview' && payments.length > 0 && (
        <div style={panelStyle}>
          <div style={panelTitle}>Recent payments</div>
          {payments.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f4f4f4' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.status === 'succeeded' ? '#1a7040' : '#c00', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{p.fighters?.first_name} {p.fighters?.last_name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-GB') : 'Pending'}{p.manual ? ' · Manual' : ''}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: p.status === 'succeeded' ? '#1a7040' : '#c00' }}>
                {(p.amount / 100).toFixed(2)} {p.currency.toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── DISCOUNT CODES TAB ── */}
      {activeTab === 'discounts' && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ ...panelTitle, margin: 0, flex: 1 }}>Discount codes</div>
            <button onClick={() => setShowAddCode(true)} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>+ Add code</button>
          </div>
          {discountCodes.length === 0 ? (
            <div style={{ color: '#888', fontSize: 13, textAlign: 'center', padding: '1.5rem' }}>No discount codes yet.</div>
          ) : discountCodes.map(code => {
            const expired = code.expires_at && new Date(code.expires_at) < new Date()
            const exhausted = code.max_uses !== null && code.uses_count >= code.max_uses
            const inactive = !code.active || expired || exhausted
            return (
              <div key={code.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f4f4f4', opacity: inactive ? 0.5 : 1 }}>
                <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '4px 10px', fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#1860a8', letterSpacing: '0.05em' }}>
                  {code.code}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                    {code.type === 'percent' ? `${code.amount}% off` : `${(code.amount / 100).toFixed(2)} off`}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
                    {code.uses_count} use{code.uses_count !== 1 ? 's' : ''}{code.max_uses !== null ? ` / ${code.max_uses} max` : ' (unlimited)'}
                    {code.expires_at ? ` · expires ${new Date(code.expires_at).toLocaleDateString('en-GB')}` : ''}
                    {expired ? ' · EXPIRED' : exhausted ? ' · EXHAUSTED' : ''}
                  </div>
                </div>
                <button onClick={async () => {
                  await supabase.from('discount_codes').update({ active: !code.active }).eq('id', code.id)
                  load()
                }} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 7, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#666', fontFamily: 'inherit' }}>
                  {code.active && !expired && !exhausted ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* ── FAMILY PLANS TAB ── */}
      {activeTab === 'families' && (
        <div style={panelStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ ...panelTitle, margin: 0, flex: 1 }}>Family groups</div>
            <button onClick={() => setShowAddFamily(true)} style={{ ...btnStyle, padding: '6px 12px', fontSize: 12 }}>+ Add group</button>
          </div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: '1rem', lineHeight: 1.6 }}>
            Group family members together. Apply a discount code to all members of the family at once. Useful for siblings or parent + child training together.
          </div>
          {familyGroups.length === 0 ? (
            <div style={{ color: '#888', fontSize: 13, textAlign: 'center', padding: '1.5rem' }}>No family groups yet.</div>
          ) : familyGroups.map(group => {
            const members = fighters.filter(f => f.family_group_id === group.id)
            return (
              <div key={group.id} style={{ background: '#f8f9fc', borderRadius: 10, padding: '12px', marginBottom: 10, border: '1px solid #eee' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>{group.name}</div>
                {members.length === 0
                  ? <div style={{ fontSize: 12, color: '#aaa' }}>No members assigned yet.</div>
                  : members.map(m => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                      <div style={{ fontSize: 13, flex: 1, color: '#444' }}>{m.first_name} {m.last_name}</div>
                      <button onClick={async () => {
                        await supabase.from('fighters').update({ family_group_id: null }).eq('id', m.id)
                        load()
                      }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c00', fontSize: 12 }}>Remove</button>
                    </div>
                  ))
                }
                {/* Add fighter to group */}
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <select id={`add-to-${group.id}`} style={{ flex: 1, padding: '5px 8px', borderRadius: 7, border: '1px solid #ddd', fontSize: 12 }}>
                    <option value="">Add member...</option>
                    {fighters.filter(f => !f.family_group_id).map(f => (
                      <option key={f.id} value={f.id}>{f.first_name} {f.last_name}</option>
                    ))}
                  </select>
                  <button onClick={async () => {
                    const sel = document.getElementById(`add-to-${group.id}`)
                    if (sel.value) { await supabase.from('fighters').update({ family_group_id: group.id }).eq('id', sel.value); load() }
                  }} style={{ ...btnStyle, padding: '5px 12px', fontSize: 12 }}>Add</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── ANALYTICS TAB (within overview) ── */}
      {activeTab === 'overview' && payments.length > 0 && (
        <div style={panelStyle}>
          <div style={panelTitle}>Revenue trend (last 6 months)</div>
          <RevenueChart payments={payments} />
        </div>
      )}

      {showAddPlan && (
        <AddPlanModal clubId={club.id} onClose={() => setShowAddPlan(false)} onSaved={() => { setShowAddPlan(false); load() }} />
      )}
      {showAddCode && (
        <AddDiscountCodeModal clubId={club.id} onClose={() => setShowAddCode(false)} onSaved={() => { setShowAddCode(false); load() }} />
      )}
      {showAddFamily && (
        <AddFamilyGroupModal clubId={club.id} onClose={() => setShowAddFamily(false)} onSaved={() => { setShowAddFamily(false); load() }} />
      )}
    </div>
  )
}

function SubRow({ sub, onRefresh, plans }) {
  const [showActions, setShowActions] = useState(false)
  const [actioning, setActioning] = useState(false)
  const [showPayHistory, setShowPayHistory] = useState(false)
  const [payments, setPayments] = useState([])
  const [showManualPay, setShowManualPay] = useState(false)
  const [manualPay, setManualPay] = useState({ amount: '', method: 'cash', notes: '', date: new Date().toISOString().split('T')[0] })

  const statusColor = {
    active: '#1a7040', overdue: '#a06000', suspended: '#c00',
    cancelled: '#888', pending: '#a06000', paused: '#1860a8', trial: '#5040b8'
  }[sub.status] || '#888'
  const statusBg = {
    active: '#eaf7f0', overdue: '#fff8e8', suspended: '#fff0f0',
    cancelled: '#f8f8f8', pending: '#fff8e8', paused: '#eef4ff', trial: '#f0eeff'
  }[sub.status] || '#f8f8f8'

  async function loadPayments() {
    const { data } = await supabase.from('payment_history').select('*')
      .eq('fighter_id', sub.fighter_id).order('created_at', { ascending: false }).limit(10)
    setPayments(data || [])
    setShowPayHistory(true)
  }

  async function recordManualPayment() {
    if (!manualPay.amount) return
    setActioning(true)
    try {
      const amount = Math.round(parseFloat(manualPay.amount) * 100)
      await supabase.from('payment_history').insert({
        fighter_id: sub.fighter_id,
        subscription_id: sub.id,
        plan_id: sub.plan_id,
        amount,
        currency: sub.membership_plans?.currency || 'eur',
        status: 'succeeded',
        paid_at: new Date(manualPay.date).toISOString(),
        manual: true,
        notes: `${manualPay.method === 'cash' ? 'Cash payment' : 'Bank transfer'}${manualPay.notes ? ' — ' + manualPay.notes : ''}`,
      })
      // If overdue/suspended/pending, reactivate
      if (['overdue','suspended','pending'].includes(sub.status)) {
        await supabase.from('member_subscriptions').update({
          status: 'active', overdue_since: null, payment_failed_count: 0,
          suspended_at: null, updated_at: new Date().toISOString()
        }).eq('id', sub.id)
      }
      setShowManualPay(false)
      setManualPay({ amount: '', method: 'cash', notes: '', date: new Date().toISOString().split('T')[0] })
      onRefresh()
    } catch (err) { alert('Error: ' + err.message) }
    setActioning(false)
  }

  async function doAction(action, extraParams = {}) {
    setActioning(true)
    try {
      if (action === 'manual_pay') {
        await supabase.from('member_subscriptions').update({
          status: 'active', overdue_since: null, payment_failed_count: 0,
          suspended_at: null, updated_at: new Date().toISOString()
        }).eq('id', sub.id)
        await supabase.from('payment_history').insert({
          fighter_id: sub.fighter_id, subscription_id: sub.id, plan_id: sub.plan_id,
          amount: sub.membership_plans?.amount || 0,
          currency: sub.membership_plans?.currency || 'eur',
          status: 'succeeded', paid_at: new Date().toISOString(),
          manual: true, notes: extraParams.notes || 'Manual payment'
        })
      } else {
        const { error } = await supabase.functions.invoke('pause-subscription', {
          body: { subscription_id: sub.id, action, ...extraParams }
        })
        if (error) throw new Error(error.message)
      }
      onRefresh()
    } catch (err) { alert('Error: ' + err.message) }
    setActioning(false); setShowActions(false)
  }

  return (
    <div style={{ borderBottom: '1px solid #f4f4f4' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{sub.fighters?.first_name} {sub.fighters?.last_name}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
            {sub.membership_plans?.name}
            {sub.next_billing_date && sub.status === 'active' ? ` · next ${sub.next_billing_date}` : ''}
            {sub.paused_until ? ` · resumes ${sub.paused_until}` : ''}
            {sub.payment_failed_count > 0 ? ` · ${sub.payment_failed_count} failed` : ''}
          </div>
        </div>
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: statusBg, color: statusColor, fontWeight: 500, textTransform: 'capitalize', flexShrink: 0 }}>
          {sub.status}
        </span>
        <button onClick={() => setShowActions(a => !a)}
          style={{ background: 'none', border: '1px solid #ddd', borderRadius: 7, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#666', fontFamily: 'inherit' }}>
          {showActions ? 'Close' : 'Actions'}
        </button>
        <button onClick={loadPayments}
          style={{ background: 'none', border: '1px solid #ddd', borderRadius: 7, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#666', fontFamily: 'inherit' }}>
          History
        </button>
        <button onClick={() => { setManualPay({ amount: sub.membership_plans?.amount ? (sub.membership_plans.amount/100).toFixed(2) : '', method: 'cash', notes: '', date: new Date().toISOString().split('T')[0] }); setShowManualPay(true) }}
          style={{ background: '#1a7040', border: 'none', borderRadius: 7, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#fff', fontFamily: 'inherit' }}>
          + Pay
        </button>
      </div>

      {showActions && (
        <div style={{ background: '#f8f9fc', borderRadius: 8, padding: '10px 12px', marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {sub.status === 'active' && (
            <>
              <button disabled={actioning} onClick={() => {
                const weeks = prompt('Pause for how many weeks?', '4')
                const reason = prompt('Reason (optional):', '')
                if (weeks) doAction('pause', { pause_weeks: parseInt(weeks), reason })
              }} style={aBtn('#1860a8')}>Pause</button>
              <button disabled={actioning} onClick={() => {
                if (window.confirm('Cancel this membership? Member keeps access until period end.')) doAction('cancel')
              }} style={aBtn('#c00')}>Cancel</button>
            </>
          )}
          {sub.status === 'paused' && (
            <button disabled={actioning} onClick={() => doAction('resume')} style={aBtn('#1a7040')}>Resume</button>
          )}
          {['overdue','suspended','pending'].includes(sub.status) && (
            <button disabled={actioning} onClick={() => {
              const notes = prompt('Payment notes:', 'Cash payment received')
              if (notes !== null) doAction('manual_pay', { notes })
            }} style={aBtn('#1a7040')}>Mark paid (cash/transfer)</button>
          )}
          {actioning && <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>Processing...</span>}
        </div>
      )}

      {showManualPay && (
        <div style={{ background: '#f0f6ff', borderRadius: 8, padding: '12px', marginBottom: 8, border: '1px solid #c8d8f8' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1860a8', marginBottom: 10 }}>Record payment — {sub.fighters?.first_name} {sub.fighters?.last_name}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: '1 1 80px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Amount ({sub.membership_plans?.currency?.toUpperCase() || 'EUR'})</div>
              <input type="number" value={manualPay.amount} onChange={e => setManualPay(p => ({ ...p, amount: e.target.value }))}
                placeholder="0.00" step="0.01" min="0"
                style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ flex: '1 1 100px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Method</div>
              <select value={manualPay.method} onChange={e => setManualPay(p => ({ ...p, method: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, background: '#fff' }}>
                <option value="cash">Cash</option>
                <option value="bank">Bank transfer</option>
              </select>
            </div>
            <div style={{ flex: '1 1 110px' }}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Date</div>
              <input type="date" value={manualPay.date} onChange={e => setManualPay(p => ({ ...p, date: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Notes (optional)</div>
            <input value={manualPay.notes} onChange={e => setManualPay(p => ({ ...p, notes: e.target.value }))}
              placeholder="e.g. 3 months paid in advance"
              style={{ width: '100%', padding: '6px 8px', borderRadius: 7, border: '1px solid #c8d8f8', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={recordManualPayment} disabled={actioning || !manualPay.amount}
              style={{ background: '#1a7040', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              {actioning ? 'Saving...' : 'Record payment'}
            </button>
            <button onClick={() => setShowManualPay(false)}
              style={{ background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 7, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showPayHistory && (
        <div style={{ background: '#f8f9fc', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#444', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
            Payment history
            <button onClick={() => setShowPayHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 14 }}>×</button>
          </div>
          {payments.length === 0
            ? <div style={{ fontSize: 12, color: '#aaa' }}>No payments yet.</div>
            : payments.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: p.status === 'succeeded' ? '#1a7040' : '#c00' }} />
                <div style={{ flex: 1, fontSize: 11 }}>
                  <span style={{ color: '#444' }}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-GB') : 'Pending'}</span>
                  {p.manual && <span style={{ color: '#1860a8', marginLeft: 4 }}>· Manual</span>}
                  {p.notes && <span style={{ color: '#888', marginLeft: 4 }}>· {p.notes}</span>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: p.status === 'succeeded' ? '#1a7040' : '#c00' }}>
                  {(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  )
}

function aBtn(color) {
  return { background: color, color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }
}


function AssignRow({ fighter, plans, onAssigned }) {
  const [selectedPlan, setSelectedPlan] = useState('')
  const [assigning, setAssigning] = useState(false)

  async function assign() {
    if (!selectedPlan) return
    setAssigning(true)
    const plan = plans.find(p => p.id === selectedPlan)
    const nextBilling = new Date()
    if (plan?.interval === 'month') nextBilling.setMonth(nextBilling.getMonth() + 1)
    if (plan?.interval === 'year') nextBilling.setFullYear(nextBilling.getFullYear() + 1)

    await supabase.from('member_subscriptions').insert({
      fighter_id: fighter.id,
      plan_id: selectedPlan,
      status: 'pending',
      start_date: new Date().toISOString().split('T')[0],
      next_billing_date: nextBilling.toISOString().split('T')[0],
    })
    setAssigning(false)
    onAssigned()
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f8f8f8' }}>
      <div style={{ fontSize: 13, color: '#444', flex: 1 }}>{fighter.first_name} {fighter.last_name}</div>
      <select value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}
        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, background: '#fff', maxWidth: 160 }}>
        <option value="">Assign plan...</option>
        {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <button onClick={assign} disabled={!selectedPlan || assigning}
        style={{ ...btnStyle, padding: '4px 10px', fontSize: 11 }}>
        {assigning ? '...' : 'Assign'}
      </button>
    </div>
  )
}

function AddPlanModal({ clubId, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', description: '', amount: '', currency: 'eur',
    interval: 'month', fighter_type: 'all'
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function save() {
    if (!form.name || !form.amount) return
    setSaving(true)
    await supabase.from('membership_plans').insert({
      club_id: clubId,
      name: form.name,
      description: form.description,
      amount: Math.round(parseFloat(form.amount) * 100), // convert to cents
      currency: form.currency,
      interval: form.interval,
      fighter_type: form.fighter_type,
      active: true,
    })
    setSaving(false)
    onSaved()
  }

  return (
    <Modal title="Add membership plan" onClose={onClose}>
      <Grid>
        <FormField label="Plan name" fullWidth>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Kids Monthly" style={{ width: '100%' }} />
        </FormField>
        <FormField label="Price (e.g. 40.00)">
          <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="40.00" step="0.01" min="0" />
        </FormField>
        <FormField label="Currency">
          <select value={form.currency} onChange={e => set('currency', e.target.value)} style={{ width: '100%' }}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </FormField>
        <FormField label="Interval">
          <select value={form.interval} onChange={e => set('interval', e.target.value)} style={{ width: '100%' }}>
            {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
        </FormField>
        <FormField label="For">
          <select value={form.fighter_type} onChange={e => set('fighter_type', e.target.value)} style={{ width: '100%' }}>
            <option value="all">All members</option>
            <option value="kids">Kids only</option>
            <option value="adult">Adults only</option>
          </select>
        </FormField>
        <FormField label="Description (optional)" fullWidth>
          <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Unlimited classes, Mon/Wed/Fri" style={{ width: '100%' }} />
        </FormField>
      </Grid>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Add plan'}</button>
        <button onClick={onClose} style={btnSecStyle}>Cancel</button>
      </div>
    </Modal>
  )
}

function StatBox({ label, value, color, bg = '#f8f9fc' }) {
  return (
    <div style={{ background: bg, borderRadius: 10, padding: '14px', border: '1px solid #eee' }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
    </div>
  )
}

const panelStyle = { background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #eee', marginBottom: '1.25rem' }
const panelTitle = { fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: '1rem' }

// ── REVENUE CHART ──────────────────────────────────────────────────────────
function RevenueChart({ payments }) {
  const months = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date()
    d.setMonth(d.getMonth() - i)
    months.push({ label: d.toLocaleString('en-GB', { month: 'short' }), year: d.getFullYear(), month: d.getMonth() })
  }

  const data = months.map(m => {
    const total = payments
      .filter(p => {
        if (p.status !== 'succeeded' || !p.paid_at) return false
        const d = new Date(p.paid_at)
        return d.getMonth() === m.month && d.getFullYear() === m.year
      })
      .reduce((sum, p) => sum + p.amount, 0)
    return { ...m, total }
  })

  const maxVal = Math.max(...data.map(d => d.total), 1)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120, marginBottom: 8 }}>
        {data.map((m, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 10, color: '#888', fontWeight: 500 }}>
              {m.total > 0 ? `${(m.total / 100).toFixed(0)}` : ''}
            </div>
            <div style={{
              width: '100%', borderRadius: '4px 4px 0 0',
              background: i === data.length - 1 ? '#1860a8' : '#c8d8f0',
              height: m.total > 0 ? `${Math.max(8, (m.total / maxVal) * 90)}px` : '4px',
              transition: 'height 0.3s'
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {data.map((m, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 11, color: '#888' }}>{m.label}</div>
        ))}
      </div>
    </div>
  )
}

// ── ADD DISCOUNT CODE MODAL ────────────────────────────────────────────────
function AddDiscountCodeModal({ clubId, onClose, onSaved }) {
  const [form, setForm] = useState({ code: '', type: 'percent', amount: '', max_uses: '', expires_at: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  function generateCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    set('code', Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''))
  }

  async function save() {
    if (!form.code || !form.amount) { setError('Code and amount are required'); return }
    setSaving(true); setError('')
    const { error: dbErr } = await supabase.from('discount_codes').insert({
      club_id: clubId,
      code: form.code.toUpperCase().trim(),
      type: form.type,
      amount: form.type === 'percent' ? parseInt(form.amount) : Math.round(parseFloat(form.amount) * 100),
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      expires_at: form.expires_at || null,
      active: true,
    })
    if (dbErr) { setError(dbErr.message); setSaving(false); return }
    setSaving(false); onSaved()
  }

  return (
    <Modal title="Add discount code" onClose={onClose}>
      <Grid>
        <FormField label="Code" fullWidth>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())}
              placeholder="SUMMER25" style={{ flex: 1, fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.05em' }} />
            <button onClick={generateCode} style={{ ...btnSecStyle, whiteSpace: 'nowrap' }}>Generate</button>
          </div>
        </FormField>
        <FormField label="Type">
          <select value={form.type} onChange={e => set('type', e.target.value)} style={{ width: '100%' }}>
            <option value="percent">Percentage off</option>
            <option value="fixed">Fixed amount off</option>
          </select>
        </FormField>
        <FormField label={form.type === 'percent' ? 'Percent off (e.g. 25)' : 'Amount off (e.g. 10.00)'}>
          <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)}
            placeholder={form.type === 'percent' ? '25' : '10.00'} min="0" />
        </FormField>
        <FormField label="Max uses (blank = unlimited)">
          <input type="number" value={form.max_uses} onChange={e => set('max_uses', e.target.value)} placeholder="Unlimited" min="1" />
        </FormField>
        <FormField label="Expires (optional)">
          <input type="date" value={form.expires_at} onChange={e => set('expires_at', e.target.value)} />
        </FormField>
      </Grid>
      {error && <div style={{ color: '#c00', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Add code'}</button>
        <button onClick={onClose} style={btnSecStyle}>Cancel</button>
      </div>
    </Modal>
  )
}

// ── ADD FAMILY GROUP MODAL ─────────────────────────────────────────────────
function AddFamilyGroupModal({ clubId, onClose, onSaved }) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!name.trim()) return
    setSaving(true)
    await supabase.from('family_groups').insert({ club_id: clubId, name: name.trim() })
    setSaving(false); onSaved()
  }

  return (
    <Modal title="Add family group" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#666', fontWeight: 500, marginBottom: 5 }}>Family name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Horvat family"
          style={{ width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
        <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>You can add members to the group after creating it.</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Create group'}</button>
        <button onClick={onClose} style={btnSecStyle}>Cancel</button>
      </div>
    </Modal>
  )
}
